import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { MessageCircle } from "lucide-react";
import { format, parseISO } from "date-fns";
import { sv } from "date-fns/locale";

interface Props {
  playerId: string;
}

export default function PlayerFeedbackHistory({ playerId }: Props) {
  const { data: feedback = [], isLoading } = useQuery({
    queryKey: ["player-feedback-history", playerId],
    queryFn: async () => {
      const { data } = await supabase
        .from("player_feedback")
        .select(
          "id, comment, skill_ratings, created_at, profiles!player_feedback_coach_id_fkey(full_name), training_sessions!inner(session_date, start_time, training_groups(name, color))"
        )
        .eq("player_id", playerId)
        .not("comment", "is", null)
        .order("created_at", { ascending: false })
        .limit(30);
      return data ?? [];
    },
  });

  if (isLoading) return <p className="text-sm text-muted-foreground">Laddar feedback...</p>;
  if (feedback.length === 0)
    return (
      <div className="text-center py-8 text-muted-foreground">
        <MessageCircle className="h-8 w-8 mx-auto mb-2 opacity-40" />
        <p className="text-sm">Ingen individuell feedback ännu.</p>
      </div>
    );

  return (
    <div className="space-y-3">
      {feedback.map((f: any) => {
        const s = f.training_sessions;
        return (
          <Card key={f.id}>
            <CardContent className="py-3 px-4 space-y-1.5">
              <div className="flex items-center gap-2 text-sm flex-wrap">
                {s?.training_groups?.color && (
                  <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: s.training_groups.color }} />
                )}
                <span className="font-medium">{s?.training_groups?.name}</span>
                <span className="text-muted-foreground">·</span>
                <span className="text-muted-foreground">
                  {s?.session_date ? format(parseISO(s.session_date), "d MMM yyyy", { locale: sv }) : ""}
                </span>
              </div>
              <p className="text-sm italic">"{f.comment}"</p>
              {f.profiles?.full_name && (
                <p className="text-xs text-muted-foreground">— {f.profiles.full_name}</p>
              )}
              {f.skill_ratings && Object.keys(f.skill_ratings).length > 0 && (
                <div className="flex gap-1 flex-wrap mt-1">
                  <Badge variant="secondary" className="text-[10px]">Bedömning bifogad</Badge>
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
